
import React from 'react';
import { cn } from '@/lib/utils';
import { vibrate } from '@/utils/mobile';

export type TouchControlAction = 'left' | 'right' | 'up' | 'down' | 'rotate' | 'fire' | 'drop' | 'pause';

interface TouchControlButtonProps {
  action: TouchControlAction;
  label: string;
  icon?: string;
  onPress: (action: TouchControlAction) => void;
  onRelease?: (action: TouchControlAction) => void;
  className?: string;
  color?: 'blue' | 'green' | 'red' | 'gray';
  shape?: 'circle' | 'rect';
}

const TouchControlButton: React.FC<TouchControlButtonProps> = ({
  action,
  label,
  icon,
  onPress,
  onRelease,
  className,
  color = 'blue',
  shape = 'circle'
}) => {
  const colorClasses = {
    blue: 'bg-blue-500 active:bg-blue-600',
    green: 'bg-green-500 active:bg-green-600',
    red: 'bg-red-500 active:bg-red-600',
    gray: 'bg-gray-500 active:bg-gray-600'
  };
  
  const shapeClasses = {
    circle: 'rounded-full p-3 min-w-[40px] min-h-[40px]',
    rect: 'rounded py-2 px-4'
  };
  
  const handleTouchStart = () => {
    vibrate();
    onPress(action);
  };
  
  const handleTouchEnd = () => {
    if (onRelease) {
      onRelease(action);
    }
  };
  
  return (
    <button
      className={cn(
        'text-white shadow-md touch-manipulation flex items-center justify-center',
        colorClasses[color],
        shapeClasses[shape],
        className
      )}
      onTouchStart={handleTouchStart}
      onMouseDown={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onMouseUp={handleTouchEnd}
      onMouseLeave={handleTouchEnd}
      aria-label={label}
    >
      <span className="text-lg" aria-hidden="true">{icon || label}</span>
    </button>
  );
};

export interface MobileTouchControlsProps {
  onButtonPress: (action: TouchControlAction) => void;
  onButtonRelease?: (action: TouchControlAction) => void;
  type: 'directional' | 'arcade' | 'simple';
  className?: string;
}

const MobileTouchControls: React.FC<MobileTouchControlsProps> = ({
  onButtonPress,
  onButtonRelease,
  type,
  className
}) => {
  return (
    <div 
      className={cn(
        "absolute bottom-4 left-0 w-full flex justify-around items-center px-2",
        className
      )}
      role="group"
      aria-label="Game controls"
    >
      {type === 'directional' && (
        <>
          <TouchControlButton
            action="left"
            label="Left"
            icon="←"
            color="blue"
            onPress={onButtonPress}
            onRelease={onButtonRelease}
          />
          
          <TouchControlButton
            action="up"
            label="Up"
            icon="▲"
            color="green"
            onPress={onButtonPress}
            onRelease={onButtonRelease}
          />
          
          <TouchControlButton
            action="fire"
            label="Fire"
            shape="rect"
            color="red"
            onPress={onButtonPress}
          />
          
          <TouchControlButton
            action="right"
            label="Right"
            icon="→"
            color="blue"
            onPress={onButtonPress}
            onRelease={onButtonRelease}
          />
        </>
      )}
      
      {type === 'arcade' && (
        <>
          <div className="flex gap-4">
            <TouchControlButton
              action="left"
              label="Left"
              icon="←"
              color="blue"
              onPress={onButtonPress}
              onRelease={onButtonRelease}
            />
            
            <TouchControlButton
              action="right"
              label="Right"
              icon="→"
              color="blue"
              onPress={onButtonPress}
              onRelease={onButtonRelease}
            />
          </div>
          
          <div className="flex gap-4">
            <TouchControlButton
              action="rotate"
              label="Rotate"
              icon="↻"
              color="green"
              onPress={onButtonPress}
            />
            
            <TouchControlButton
              action="drop"
              label="Drop"
              icon="↓"
              color="red"
              onPress={onButtonPress}
            />
          </div>
        </>
      )}
      
      {type === 'simple' && (
        <TouchControlButton
          action="fire"
          label="Action"
          shape="rect"
          color="green"
          className="px-8 py-4"
          onPress={onButtonPress}
        />
      )}
    </div>
  );
};

export default MobileTouchControls;
